import { useCallback, useEffect, useRef, useState } from "react";

export type PollingStatus = "polling" | "done" | "timed-out";

// cb (callback function) expects a returned truthy value to end polling
export const usePolling = (
  cb: () => Promise<boolean | undefined>,
  maxLimit = 15, // Limits the # of callbacks until polling ends
  intervalTime = 2000
) => {
  const count = useRef(0);
  const [status, setStatus] = useState<PollingStatus>("polling");
  const interval = useRef<NodeJS.Timeout | null>(null);

  const callback = useCallback(async () => {
    count.current = count.current + 1;

    const resolved = await cb();

    if (resolved || count.current >= maxLimit) {
      interval.current && clearInterval(interval.current);
    }

    if (resolved) {
      setStatus("done");
    }

    if (count.current >= maxLimit) {
      setStatus("timed-out");
    }
  }, [cb, maxLimit]);

  useEffect(() => {
    // Fire immediately, then on each interval tick
    const id = setTimeout(() => {
      callback();
      interval.current = setInterval(callback, intervalTime);
    }, 0);

    return () => {
      clearTimeout(id);
      interval.current && clearInterval(interval.current);
    };
  }, [callback, intervalTime]);

  return status;
};
